import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Eye, EyeOff, CheckCircle, Mail, ShieldQuestion } from 'lucide-react';
import { toast } from 'sonner';
import { verifySecurityQuestion, resetPassword, requestEmailReset } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [method, setMethod] = useState(null); // null: choose, 'email', 'security'
  const [step, setStep] = useState(1); // 1: email entry, 2: security question (if security method), 3: new password, 4: success
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    security_answer: '',
    new_password: '',
    confirm_password: '',
  });
  
  const handleBack = () => {
    if (step === 1 && method) {
      setMethod(null);
    } else if (step === 1) {
      navigate('/login');
    } else {
      setStep(step - 1);
    }
  };
  
  const handleEmailReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await requestEmailReset(formData.email, window.location.origin);
      setEmailSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      // Always show success to prevent email enumeration
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSecurityEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await verifySecurityQuestion({ email: formData.email });
      setSecurityQuestion(response.data.security_question);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email not found or no security question set');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSecurityAnswer = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await verifySecurityQuestion({ 
        email: formData.email,
        security_answer: formData.security_answer 
      });
      setResetToken(response.data.reset_token);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect answer');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await resetPassword({
        reset_token: resetToken,
        new_password: formData.new_password,
      });
      setStep(4);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="hero-gradient absolute inset-0" />
      
      <header className="relative z-10 p-6">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={handleBack}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </header>
      
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src={ONIRVA_ICON} alt="ONIRVA" className="w-20 h-20 rounded-2xl" />
            </div>
            <h1 className="text-3xl font-serif font-bold mb-2">
              {step === 4 ? 'Password Reset' : emailSent ? 'Check Your Email' : 'Forgot Password'}
            </h1>
            <p className="text-muted-foreground">
              {!method && 'Choose how to reset your password'}
              {method === 'email' && !emailSent && 'We\'ll send you a reset link'}
              {method === 'email' && emailSent && 'A reset link has been sent if an account exists'}
              {method === 'security' && step === 1 && 'Enter your email to see your security question'}
              {method === 'security' && step === 2 && 'Answer your security question'}
              {method === 'security' && step === 3 && 'Create a new password'}
              {step === 4 && 'Your password has been updated'}
            </p>
          </div>
          
          {/* Method Selection */}
          {!method && (
            <div className="space-y-4">
              <button
                onClick={() => setMethod('email')}
                className="w-full glass-card p-4 flex items-center gap-4 hover:bg-primary/5 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Email Reset Link</p>
                  <p className="text-sm text-muted-foreground">Receive a link to reset your password</p>
                </div>
              </button>
              
              <button
                onClick={() => setMethod('security')}
                className="w-full glass-card p-4 flex items-center gap-4 hover:bg-primary/5 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <ShieldQuestion className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Security Question</p>
                  <p className="text-sm text-muted-foreground">Answer your security question</p>
                </div>
              </button>
            </div>
          )}
          
          {/* Email Reset Method */}
          {method === 'email' && !emailSent && (
            <form onSubmit={handleEmailReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                  data-testid="email-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full py-6"
                disabled={isLoading}
                data-testid="send-reset-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}
          
          {/* Email Sent Confirmation */}
          {method === 'email' && emailSent && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6">
                If an account exists for <span className="text-foreground font-medium">{formData.email}</span>, you will receive a password reset link shortly.
              </p>
              
              <p className="text-sm text-muted-foreground mb-6">
                The link will expire in 15 minutes.
              </p>
              
              <Button 
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/login')}
              >
                Back to Sign In
              </Button>
            </div>
          )}
          
          {/* Security Question Method - Step 1 */}
          {method === 'security' && step === 1 && (
            <form onSubmit={handleSecurityEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                  data-testid="email-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full py-6"
                disabled={isLoading}
                data-testid="continue-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </form>
          )}
          
          {/* Security Question Method - Step 2 */}
          {method === 'security' && step === 2 && (
            <form onSubmit={handleSecurityAnswer} className="space-y-4">
              <div className="glass-card p-4 mb-4">
                <p className="text-sm text-muted-foreground mb-1">Security Question:</p>
                <p className="font-medium">{securityQuestion}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="security_answer">Your Answer</Label>
                <Input
                  id="security_answer"
                  type="text"
                  placeholder="Enter your answer"
                  value={formData.security_answer}
                  onChange={(e) => setFormData({ ...formData, security_answer: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                  data-testid="security-answer-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full py-6"
                disabled={isLoading}
                data-testid="verify-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Answer'
                )}
              </Button>
            </form>
          )}
          
          {/* New Password - Step 3 */}
          {method === 'security' && step === 3 && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 6 characters"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    required
                    minLength={6}
                    className="bg-muted/50 border-border pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                  data-testid="confirm-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full rounded-full py-6"
                disabled={isLoading}
                data-testid="reset-password-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
          
          {/* Success - Step 4 */}
          {step === 4 && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
              </div>
              
              <Button 
                className="w-full rounded-full py-6"
                onClick={() => navigate('/login')}
                data-testid="back-to-login-btn"
              >
                Back to Sign In
              </Button>
            </div>
          )}
          
          {!method && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
